import { observer } from 'mobx-react-lite';
import React from 'react';
import { useMst } from '../../../models/stores/Root';
import { IPaymentMethodModel } from '../../../models/stores/sub-stores/Configuration/PaymentMethodModel';
import { PayPalOnApproveData, PayPalOnClickData } from '../../../types/PayPal';
import PayPalMessagesContainer from '../../framework/PayPalMessagesContainer';
import AmazonPayCheckoutButton from '../../payment/AmazonPayCheckoutButton';
import PayPalCheckoutButton from '../../payment/PayPalCheckoutButton';
import WrappedStripePaymentRequestButton from '../../payment/WrappedStripePaymentRequestButton';
import { IPayPalExpress } from '../../../models/stores/sub-stores/Configuration';
import CardLogo from '../../content/CardLogo';

export const expressCheckoutTestID = 'expressCheckoutTestId';

const ExpressCheckout: React.FC = observer(() => {
  const rootStore = useMst();

  const { configuration, cart } = rootStore;

  const paypalConfig: IPayPalExpress = configuration.paypalExpress;

  const funding = configuration.features.enablePayPalPpcp
    ? paypalConfig.fundingToEnable
    : undefined;

  type ButtonType = 'PAYPAL' | 'PAYLATER' | 'VENMO';

  let payPalButtons: string[] = [];

  if (funding?.length) {
    payPalButtons = funding.split(',');
    payPalButtons.unshift('paypal');
  } else if (configuration.canDisplayPayPalButtons) {
    payPalButtons = ['paypal'];
  }
  const displayAmazonPayButton =
    configuration.payments.enabledPaymentMethods.some(
      (m: IPaymentMethodModel) => m.methodType === 'Amazon Pay'
    );

  const handlePayPalOnCreateOrder = async (): Promise<string> => {
    return rootStore.createPayPalOrder();
  };

  const handlePayPalOnApprove = async (
    data: PayPalOnApproveData
  ): Promise<void> => {
    cart.paypalInfo.setIsVenmo(cart.paypalInfo.isVenmoPending);
    rootStore.send({ type: 'PAYPAL_ON_APPROVED', paypalParams: data });
  };

  const handlePayPalOnClick = async (
    data: PayPalOnClickData
  ): Promise<void> => {
    cart.paypalInfo.setIsVenmoPending(
      data.fundingSource?.toLowerCase() === 'venmo'
    );
  };

  const handleAmazonPayOnLoaded = (): void => {
    rootStore.setAmazonLoaded(true);
  };

  const handleAmazonPayOnAuthorize = async (
    accessToken: string
  ): Promise<void> => {
    rootStore.send({
      type: 'AMAZONPAY_ON_AUTHORIZE',
      amazonPayToken: accessToken,
    });
  };

  let expressItems = 0;

  if (configuration.canDisplayPayPalButtons && payPalButtons?.length) {
    expressItems += payPalButtons.length;
  }
  if (
    configuration.payments.isVpayEnabled &&
    configuration.payments.stripeWalletEnabled
  ) {
    expressItems += 1;
  }
  if (displayAmazonPayButton) {
    expressItems += 1;
  }

  let gridClasses = '';

  if (expressItems >= 5) {
    gridClasses = '2xl:grid-cols-4 xl:grid-cols-3 lg:grid-cols-2 gap-4';
  } else if (expressItems === 4) {
    gridClasses = '2xl:grid-cols-4 lg:grid-cols-2 gap-4';
  } else if (expressItems === 3) {
    gridClasses = 'xl:grid-cols-3 gap-4';
  } else if (expressItems === 2) {
    gridClasses = 'lg:grid-cols-2 gap-4';
  }

  return (
    <div data-testid={expressCheckoutTestID}>
      {expressItems > 0 ? (
        <div className="text-center p-2">
          <fieldset className="inline-flex flex-col justify-center items-center mb-8 checkout-panel-border border px-4 pb-4 pt-2">
            <legend className="checkout-panel-section-header mx-auto font-bold px-2">
              Express Checkout
            </legend>

            <div className={`w-full grid grid-cols-1 ${gridClasses}`}>
              {configuration.canDisplayPayPalButtons &&
              !cart.cartToggleOpen &&
              payPalButtons.length
                ? payPalButtons.map(button => (
                    <PayPalCheckoutButton
                      key={button}
                      buttonType={button.toUpperCase() as ButtonType}
                      buttonMethods={{
                        createOrder: handlePayPalOnCreateOrder,
                        onApprove: handlePayPalOnApprove,
                        onClick: handlePayPalOnClick,
                      }}
                    />
                  ))
                : null}
              {configuration.canDisplayPayPalButtons &&
                !cart.cartToggleOpen && <PayPalMessagesContainer />}

              {configuration.payments.isVpayEnabled &&
                configuration.payments.stripeWalletEnabled && (
                  <WrappedStripePaymentRequestButton />
                )}
              {displayAmazonPayButton && (
                <AmazonPayCheckoutButton
                  clientId={configuration.amazonPay.clientId}
                  sellerId={configuration.amazonPay.sellerId}
                  isSandbox={configuration.amazonPay.isSandbox}
                  isLoaded={rootStore.isAmazonPayLoaded}
                  onAuthorize={handleAmazonPayOnAuthorize}
                  onLoaded={handleAmazonPayOnLoaded}
                />
              )}
            </div>
          </fieldset>

          <div className="flex items-center justify-center mb-4 gap-y-4">
            <span className="hidden checkout-panel-divider lg:block" />
            <span className="checkout-panel-section-header font-bold px-2 lg:whitespace-nowrap">
              {configuration.labels.expressDivider || 'OR'}
            </span>
            <span className="hidden checkout-panel-divider lg:block" />
          </div>

          {configuration.features.enableExpressCheckoutCards && (
            <div className="flex justify-center gap-4 mb-4">
              <CardLogo className="express-cards" cardTypeName="visa" />
              <CardLogo className="express-cards" cardTypeName="mastercard" />
              <CardLogo className="express-cards" cardTypeName="discover" />
              <CardLogo className="express-cards" cardTypeName="amex" />
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
});

export default ExpressCheckout;
